import React, { useRef, useState, useEffect } from "react";
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Star,
  Sparkles,
  AlertTriangle,
  TrendingUp,
} from "lucide-react";
import { ImagePath } from "../../assests";
import CommonButton from "../CommonButton";
import CartButton from "./CartButton";
import CountdownTimer from "./ProductDetailComponent.tsx/CountdownTimer";
import { ProductItem } from "../../utils/Customer/CustomerTypes";
import { CartItem } from "./ProductDetailComponent.tsx/CartDrawer";

interface ProductGridSectionProp {
  showLogo?: boolean;
  title?: React.ReactNode;
  highlightText?: string;
  highlightClassName?: string;
  handleProductPage?: (product: ProductItem) => void;
  products: ProductItem[];
  headingPosition?: "left" | "center" | "right";
  handleAddToCart: (product: ProductItem) => void;
  handleIncrementToCart?: (product: ProductItem) => void;
  handleDecrementFromCart?: (product: ProductItem) => void;
  cartItems?: CartItem[];
  displayMode?: "grid" | "carousel";
}

const ProductGridSection: React.FC<ProductGridSectionProp> = ({
  products,
  handleProductPage,
  handleAddToCart,
  handleIncrementToCart,
  handleDecrementFromCart,
  cartItems = [],
  displayMode = "grid",
}) => {
  const [activeIndex, setActiveIndex] = useState<{ [key: number]: number }>({});
  const [timers, setTimers] = useState<{ [key: number]: number }>({});
  const [cartQty, setCartQty] = useState<{ [key: number]: number }>({});
  const [activeCartIndex, setActiveCartIndex] = useState<number | null>(null);

  // Track active marketing text index per product for continuous flipping
  const [marketingIndices, setMarketingIndices] = useState<{
    [key: number]: number;
  }>({});

  // Track hover state for image slideshow
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const carouselRef = useRef<HTMLDivElement>(null);

  const scrollCarousel = (direction: "left" | "right") => {
    if (!carouselRef.current) return;
    const cardWidth = 240;
    const scrollAmount = direction === "right" ? cardWidth * 2 : -cardWidth * 2;
    carouselRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
  };

  useEffect(() => {
    const initialTimers: Record<number, number> = {};
    products.forEach((product, index) => {
      initialTimers[index] = product.remainingSaleSeconds ?? 0;
    });
    setTimers(initialTimers);
  }, [products]);

  // ─── Continuous Flipping Marketing Text Effect (Every 2s) ───────────────────
  useEffect(() => {
    const interval = setInterval(() => {
      setMarketingIndices((prev) => {
        const nextIndices: Record<number, number> = { ...prev };
        products.forEach((product, index) => {
          const texts =
            product.marketingTexts || (product as any).MarketingTexts;
          if (texts && Array.isArray(texts) && texts.length > 1) {
            const current = prev[index] ?? 0;
            nextIndices[index] = (current + 1) % texts.length;
          }
        });
        return nextIndices;
      });
    }, 2000);

    return () => clearInterval(interval);
  }, [products]);

  // ─── Auto Image Transition On Hover (Every 2s) ─────────────────────────────
  useEffect(() => {
    if (hoveredIndex === null) return;

    const product = products[hoveredIndex];
    if (!product) return;

    const subImagesList = Array.isArray(product.subimage)
      ? product.subimage
      : [];
    const images = Array.from(
      new Set([product.productimage, ...subImagesList].filter(Boolean)),
    );

    if (images.length <= 1) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => {
        const current = prev[hoveredIndex] ?? 0;
        return {
          ...prev,
          [hoveredIndex]: (current + 1) % images.length,
        };
      });
    }, 2000);

    return () => clearInterval(interval);
  }, [hoveredIndex, products]);

  const handleAdd = (index: number) => {
    setCartQty((prev) => ({ ...prev, [index]: 1 }));
  };

  const handleIncrement = (index: number) => {
    setCartQty((prev) => {
      const current = prev[index] || 0;
      if (current >= 10) return prev;
      return { ...prev, [index]: current + 1 };
    });
  };

  const handleDecrement = (index: number) => {
    setCartQty((prev) => {
      const current = prev[index];
      if (current === 1) {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      }
      return { ...prev, [index]: current - 1 };
    });
  };
  const renderCard = (product: ProductItem, index: number) => {
    const subImagesList = Array.isArray(product.subimage)
      ? product.subimage
      : [];
    const images = Array.from(
      new Set([product.productimage, ...subImagesList].filter(Boolean)),
    );

    const currentIndex = activeIndex[index] ?? 0;
    const currentImage = images[currentIndex] || ImagePath.NoImage;
    const originalPrice = Number(product.price ?? 0);
    const salePrice = Number(product.saleprice ?? 0);

    const hasSale =
      salePrice > 0 && originalPrice > 0 && salePrice < originalPrice;

    const discountPercentage = hasSale
      ? Number(product.discountPercentage) > 0
        ? Math.round(Number(product.discountPercentage))
        : Math.round(((originalPrice - salePrice) / originalPrice) * 100)
      : 0;

    const stockQuantity = product.quantity ?? 0;
    const isOutOfStock = product.inStock === false || stockQuantity <= 0;
    const isLowStock =
      !isOutOfStock && stockQuantity > 0 && stockQuantity <= 10;

    // Marketing Texts Flip handling
    const marketingTexts: string[] =
      product.marketingTexts || (product as any).MarketingTexts || [];
    const currentMarketingTextIndex = marketingIndices[index] ?? 0;
    const currentMarketingText = marketingTexts[currentMarketingTextIndex];

    const serverCartItem = cartItems.find(
      (item) => item.productId === product.ProductId,
    );
    const isServerDriven = cartItems !== undefined;
    const qty = isServerDriven
      ? (serverCartItem?.quantity ?? 0)
      : (cartQty[index] ?? 0);

    return (
      <div
        key={`${product.ProductId}-${index}`}
        onClick={() => handleProductPage?.(product)}
        onMouseEnter={() => setHoveredIndex(index)}
        onMouseLeave={() => {
          setHoveredIndex(null);
          setActiveIndex((prev) => ({ ...prev, [index]: 0 }));
        }}
        className={`group flex flex-col justify-between border border-gray-200 bg-white rounded-2xl p-2.5 select-none cursor-pointer transition-all duration-200 hover:shadow-lg ${
          displayMode === "carousel"
            ? "w-[200px] sm:w-[220px] shrink-0"
            : "w-full max-w-[280px]"
        }`}
      >
        {/* Top Image Container - Flex Grow fills extra vertical room */}
        <div
          className={`relative rounded-t-2xl flex items-center justify-center overflow-hidden mb-2 group/img ${
            displayMode === "carousel"
              ? "h-[160px] sm:h-[180px]"
              : "h-[180px] md:h-[200px] lg:h-[220px]"
          }`}
        >
          <div className="relative h-full w-full bg-white flex items-center justify-center overflow-hidden p-2">
            <img
              src={currentImage}
              alt={product.productname}
              onError={(e) => {
                e.currentTarget.src = ImagePath.NoImage;
              }}
              className={`max-h-full max-w-full object-contain transition-all duration-500 group-hover:scale-105 ${
                isOutOfStock ? "opacity-50 grayscale" : ""
              }`}
            />
          </div>

          {/* Out of Stock Overlay Badge */}
          {isOutOfStock && (
            <div className="absolute top-2 left-2 z-20 bg-rose-600 text-white text-[10px] sm:text-xs font-extrabold px-2 py-0.5 rounded-md shadow-sm uppercase tracking-wider">
              Sold Out
            </div>
          )}
          {product?.rating != null && product?.rating > 0 && (
            <div className="absolute bottom-0 left-0 z-10 bg-blue-800 backdrop-blur px-2 py-1 rounded-r-lg flex items-center gap-1 shadow-sm">
              <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
              <span className="text-sm font-semibold text-white">
                {product.rating.toFixed(1)}
              </span>
            </div>
          )}
          {/* Rating Badge */}

          {/* Manual Navigation Arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveIndex((prev) => {
                    const current = prev[index] ?? 0;
                    return {
                      ...prev,
                      [index]: current === 0 ? images.length - 1 : current - 1,
                    };
                  });
                }}
                className="absolute left-1 top-1/2 -translate-y-1/2 z-10 bg-white/80 backdrop-blur p-1 rounded-full shadow opacity-0 group-hover/img:opacity-100 transition"
              >
                <ChevronLeft className="w-3.5 h-3.5 text-gray-800" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveIndex((prev) => {
                    const current = prev[index] ?? 0;
                    return {
                      ...prev,
                      [index]: current === images.length - 1 ? 0 : current + 1,
                    };
                  });
                }}
                className="absolute right-1 top-1/2 -translate-y-1/2 z-10 bg-white/80 backdrop-blur p-1 rounded-full shadow opacity-0 group-hover/img:opacity-100 transition"
              >
                <ChevronRight className="w-3.5 h-3.5 text-gray-800" />
              </button>
            </>
          )}

          {/* Image Pagination Dots */}
          {images.length > 1 && activeCartIndex !== index && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-10 bg-white/70 px-2 py-1 rounded-full opacity-0 group-hover/img:opacity-100 transition duration-300">
              {images.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${
                    i === currentIndex ? "w-4 bg-black" : "w-2 bg-black/40"
                  }`}
                />
              ))}
            </div>
          )}

          {timers[index] > 0 && !isOutOfStock && (
            <CountdownTimer
              totalSeconds={timers[index]}
              className="absolute top-3 right-3"
            />
          )}
        </div>

        {/* Card Content Area - Flex-1 ensures bottom alignment */}
        <div className="p-1 flex flex-col flex-1 justify-between gap-1.5">
          <div>
            {/* Product Title */}
            <p className="text-sm font-semibold line-clamp-2 text-slate-800 leading-snug">
              {product.productname}
            </p>

            {/* Marketing or Stock Tag (Collapses when empty so no awkward gaps) */}
            {(currentMarketingText || isLowStock) && (
              <div className="mt-1 flex items-center overflow-hidden min-h-[20px]">
                {currentMarketingText ? (
                  <div
                    key={currentMarketingTextIndex}
                    className="flex items-center gap-1.5 text-xs text-slate-600 font-medium animate-flip"
                  >
                    {currentMarketingText.toLowerCase().includes("few") ||
                    currentMarketingText.toLowerCase().includes("stock") ? (
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                    ) : currentMarketingText.toLowerCase().includes("sale") ||
                      currentMarketingText
                        .toLowerCase()
                        .includes("trending") ? (
                      <TrendingUp className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    ) : (
                      <Sparkles className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                    )}
                    <span className="truncate">{currentMarketingText}</span>
                  </div>
                ) : isLowStock ? (
                  <div className="flex items-center gap-1.5 text-xs text-amber-600 font-medium">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                    <span>Only {stockQuantity} Left</span>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {/* Pricing and Action Button Area */}
          <div className="flex items-end justify-between pt-1 border-t border-gray-50 mt-1">
            <div className="flex flex-col min-h-[40px] justify-end">
              {hasSale ? (
                <>
                  {/* Original Price */}
                  <span className="text-[11px] text-gray-400 line-through leading-none">
                    AED {originalPrice.toFixed(0)}
                  </span>

                  {/* Sale Price + Discount */}
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-sm sm:text-base font-bold text-slate-900 leading-tight">
                      Sale AED {salePrice.toFixed(0)}
                    </span>

                    {discountPercentage > 0 && (
                      <span
                        className="
                inline-flex
                items-center
                rounded-md
                bg-rose-50
                px-1.5
                py-0.5
                text-[9px]
                sm:text-[10px]
                font-bold
                text-rose-600
                whitespace-nowrap
              "
                      >
                        {discountPercentage}% OFF
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <span className="text-sm sm:text-base font-bold text-slate-900 leading-tight">
                  AED {originalPrice.toFixed(0)}
                </span>
              )}
            </div>

            <div onClick={(e) => e.stopPropagation()}>
              {isOutOfStock ? (
                <div
                  className="
      inline-flex
      items-center
      justify-center
      min-w-[76px]
      h-8
      px-3
      rounded-lg
      bg-slate-50
      border
      border-slate-200
      text-[10px]
      font-semibold
      text-slate-400
      uppercase
      tracking-wide
      select-none
    "
                >
                 Sold Out
                </div>
              ) : (
                <CartButton
                  qty={qty}
                  onAdd={() => {
                    handleAdd(index);
                    setActiveCartIndex(index);
                    handleAddToCart(product);
                    setTimeout(() => setActiveCartIndex(null), 1500);
                  }}
                  onIncrement={() => {
                    handleIncrement(index);
                    handleIncrementToCart?.(product);
                  }}
                  onDecrement={() => {
                    handleDecrement(index);
                    handleDecrementFromCart?.(product);
                  }}
                />
              )}
            </div>
          </div>

          {/* Cart Toast Feedback */}
          {activeCartIndex === index && (
            <div className="pt-1 animate-toast">
              <div className="flex items-center justify-center gap-1.5 text-green-600 text-xs font-medium">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Added to Cart
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (displayMode === "carousel") {
    return (
      <section className="relative w-full group/carousel py-2">
        <button
          onClick={() => scrollCarousel("left")}
          className="absolute -left-3 top-1/2 -translate-y-1/2 z-30 bg-white border border-gray-200 shadow-md hover:shadow-lg rounded-full p-2 transition hover:scale-105 active:scale-95"
          aria-label="Scroll left"
        >
          <ChevronLeft className="w-5 h-5 text-gray-700" />
        </button>

        <div
          ref={carouselRef}
          className="w-full overflow-x-auto px-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          <div className="flex flex-nowrap gap-3">
            {products.map((product, index) => renderCard(product, index))}
          </div>
        </div>

        <button
          onClick={() => scrollCarousel("right")}
          className="absolute -right-3 top-1/2 -translate-y-1/2 z-30 bg-white border border-gray-200 shadow-md hover:shadow-lg rounded-full p-2 transition hover:scale-105 active:scale-95"
          aria-label="Scroll right"
        >
          <ChevronRight className="w-5 h-5 text-gray-700" />
        </button>
      </section>
    );
  }

  return (
    <section className="relative overflow-hidden">
      <div className="relative z-10">
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 pt-2">
          {products.map((product, index) => renderCard(product, index))}
        </div>
        <div className="flex items-center justify-center md:hidden pt-3">
          <CommonButton
            text="View All Products"
            icon={<ChevronRight size={16} />}
            iconPosition="right"
          />
        </div>
      </div>
    </section>
  );
};

export default ProductGridSection;
